import { Component } from '@theme/component';
import { observeCollectionGridSlotMetrics } from '@theme/collection-grid-slot-metrics';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

const SLOT_METRICS_LOG = '[slot-metrics]';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {(() => void) | null} */
  #collectionGridSlotMetricsCleanup = null;

  /** @type {MutationObserver | null} */
  #collectionGridSlotHostObserver = null;

  /**
   * In-flight guards for the infinite-scroll renders. The sentinel IntersectionObserver can fire
   * several times while a page fetch is pending; without these, overlapping renders append the
   * same page more than once (duplicate product cards).
   * @type {boolean}
   */
  #renderingNext = false;

  /** @type {boolean} */
  #renderingPrevious = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    if (this.#isInfiniteScrollEnabled()) {
      requestIdleCallback(
        () => {
          this.#fetchPage('next');
          this.#fetchPage('previous');
        },
        { timeout: 2000 }
      );
    }
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    this.#scheduleCollectionGridSlotMetricsSetup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleCollectionGridSlotMetricsSetup();
  }

  disconnectedCallback() {
    this.#collectionGridSlotHostObserver?.disconnect();
    this.#collectionGridSlotHostObserver = null;
    this.#teardownCollectionGridSlotMetrics();
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #productGridUl() {
    const { grid } = this.refs;
    if (grid instanceof HTMLUListElement) return grid;
    const fallback = this.querySelector('ul.product-grid');
    if (fallback instanceof HTMLUListElement) return fallback;
    console.log(SLOT_METRICS_LOG, '#productGridUl: no ul', {
      refsGridType: grid ? grid.constructor?.name : 'undefined',
      ulCount: this.querySelectorAll('ul').length,
    });
    return null;
  }

  #scheduleCollectionGridSlotMetricsSetup() {
    if (this.localName === 'blog-posts-list') {
      console.log(SLOT_METRICS_LOG, '#scheduleCollectionGridSlotMetricsSetup: skip blog-posts-list');
      return;
    }

    console.log(SLOT_METRICS_LOG, '#scheduleCollectionGridSlotMetricsSetup: queued microtask', {
      tag: this.tagName,
      connected: this.isConnected,
      childElementCount: this.childElementCount,
    });

    queueMicrotask(() => {
      if (!this.isConnected) {
        console.log(SLOT_METRICS_LOG, 'microtask: host not connected, abort');
        return;
      }

      this.#collectionGridSlotHostObserver?.disconnect();
      this.#collectionGridSlotHostObserver = null;

      const attach = () => {
        const grid = this.#productGridUl();
        if (!grid) return false;
        this.#setupCollectionGridSlotMetrics();
        return true;
      };

      if (attach()) {
        console.log(SLOT_METRICS_LOG, 'microtask: attach succeeded');
        return;
      }

      console.log(SLOT_METRICS_LOG, 'microtask: no grid yet, watching subtree');

      this.#collectionGridSlotHostObserver = new MutationObserver(() => {
        if (!this.isConnected) return;
        if (attach()) {
          console.log(SLOT_METRICS_LOG, 'host MutationObserver: attach succeeded');
          this.#collectionGridSlotHostObserver?.disconnect();
          this.#collectionGridSlotHostObserver = null;
        }
      });
      this.#collectionGridSlotHostObserver.observe(this, { childList: true, subtree: true });

      setTimeout(() => {
        if (this.#collectionGridSlotHostObserver) {
          console.log(SLOT_METRICS_LOG, 'host observer: 15s timeout, disconnect');
        }
        this.#collectionGridSlotHostObserver?.disconnect();
        this.#collectionGridSlotHostObserver = null;
      }, 15000);
    });
  }

  #setupCollectionGridSlotMetrics() {
    this.#teardownCollectionGridSlotMetrics();
    const grid = this.#productGridUl();
    if (grid) {
      console.log(SLOT_METRICS_LOG, '#setupCollectionGridSlotMetrics: starting observe');
      this.#collectionGridSlotMetricsCleanup = observeCollectionGridSlotMetrics(grid);
    } else {
      console.log(SLOT_METRICS_LOG, '#setupCollectionGridSlotMetrics: no grid, skip observe');
    }
  }

  #teardownCollectionGridSlotMetrics() {
    this.#collectionGridSlotMetricsCleanup?.();
    this.#collectionGridSlotMetricsCleanup = null;
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    // A non-integer page (e.g. NaN from a card without data-page) would otherwise pass the range
    // checks below and fetch `?page=NaN`, which Shopify serves as page 1.
    if (!pageInfo || !Number.isInteger(pageInfo.page)) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } finally {
      // Resolve even when the fetch fails so a waiting render (and its in-flight guard) is released
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.#renderingNext) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    if (this.#hasRenderedPage(nextPage.page)) return;

    this.#renderingNext = true;

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      // Re-check after awaiting: the grid may have changed underneath us (filter update, another render)
      if (this.#hasRenderedPage(nextPage.page)) return;

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', nextPage.url.toString());

      this.#refreshPreorderBadges();
      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#renderingNext = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.#renderingPrevious) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;
    if (this.#hasRenderedPage(previousPage.page)) return;

    this.#renderingPrevious = true;

    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      // Re-check after awaiting: the grid may have changed underneath us (filter update, another render)
      if (this.#hasRenderedPage(previousPage.page)) return;

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }
      this.#refreshPreorderBadges();
      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#renderingPrevious = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const cards = this.#liveCards();
    const isPrevious = type === 'previous';

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    if (!Number.isInteger(currentCardPage)) return;

    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * The grid element from the live DOM (falls back to the refs snapshot if it is still attached).
   * @returns {Element | null}
   */
  #liveGrid() {
    const { grid } = this.refs;
    if (grid instanceof Element && grid.isConnected) return grid;
    return this.querySelector('[ref="grid"]');
  }

  /**
   * Cards read from the live DOM rather than `this.refs.cards`. The refs snapshot is only refreshed
   * once the base class attaches its MutationObserver, which is deferred to an idle callback with no
   * timeout. On a busy main thread that can lag long enough for the page math to compute the same
   * "next" page again after an append, which re-parses the cached HTML into a second set of cards.
   *
   * @returns {HTMLElement[]}
   */
  #liveCards() {
    const grid = this.#liveGrid();
    if (!grid) return [];
    return /** @type {HTMLElement[]} */ (Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')));
  }

  /**
   * @param {number} page
   * @returns {boolean} Whether cards for this page are already in the grid
   */
  #hasRenderedPage(page) {
    const grid = this.#liveGrid();
    return !!grid?.querySelector(`:scope > [ref="cards[]"][data-page="${page}"]`);
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Globo Preorder marks "coming soon" and pre-order cards after fetching product data asynchronously,
   * so a single call straight after an append runs before it knows the new products and those cards
   * keep the theme's "Sold out" badge. Call it again shortly after. The app ignores cards it has
   * already handled, and the method check avoids throwing while the app is still initialising.
   */
  #refreshPreorderBadges() {
    const refresh = () => {
      if (typeof Globo?.Preorder?.initCollection === 'function') Globo.Preorder.initCollection();
    };
    refresh();
    setTimeout(refresh, 1000);
    setTimeout(refresh, 2500);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Section prefetch only when infinite scroll is on (`infinite-scroll="true"` from Liquid).
   * @returns {boolean}
   */
  #isInfiniteScrollEnabled() {
    return this.getAttribute('infinite-scroll') === 'true';
  }

  /** Clears cached pages after filter-driven section updates. */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        if (this.#isInfiniteScrollEnabled()) {
          this.#fetchPage('next');
        }
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
